import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MadeBy } from "@/components/shared/MadeBy";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errorMessage, fetchContent, imgUrl } from "@/lib/api";
import { Seo } from "@/components/shared/Seo";
import { LogoMark } from "@/components/shared/Logo";

export default function Login() {
  const { admin, login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: content } = useQuery({ queryKey: ["content"], queryFn: fetchContent });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!loading && admin) return <Navigate to={location.state?.from?.pathname || "/admin"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email.trim(), password);
      toast.success("Velkomen!");
      navigate(location.state?.from?.pathname || "/admin", { replace: true });
    } catch (err) {
      setError(errorMessage(err, "Innlogginga feila"));
    } finally {
      setBusy(false);
    }
  };

  const img = content?.heim_hero?.images?.[0];

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-12">
      <Seo title="Logg inn – admin" description="Innlogging for styret i Halsnøy Kulturhus SA." noindex />
      <div className="flex flex-col px-5 py-5 sm:px-8 lg:col-span-5 lg:px-12">
        <div className="flex items-center justify-between">
          <LogoMark className="w-[200px] text-foreground" />
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="login-back-link"><ArrowLeft className="h-4 w-4" /> Til nettsida</Link>
        </div>
        <div className="flex flex-1 items-center py-16">
          <div className="w-full max-w-sm" data-testid="admin-login-card">
            <p className="t-mono text-muted-foreground">Admin for styret</p>
            <h1 className="t-h2 mt-3">Logg inn</h1>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-[13px] font-medium">E-post</Label>
                <Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-12" data-testid="admin-login-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-[13px] font-medium">Passord</Label>
                <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-12" data-testid="admin-login-password-input" />
              </div>
              {error && <p className="rounded-[8px] border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="admin-login-error">{error}</p>}
              <Button type="submit" size="xl" className="w-full justify-between" disabled={busy} data-testid="admin-login-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Logg inn <ArrowRight className="h-4 w-4" /></>}</Button>
            </form>
            <p className="mt-8 text-xs leading-relaxed text-muted-foreground">Har du gløymt passordet? Ta kontakt med ein annan i styret som kan byte det under Innstillingar, eller med den som drifta nettsida.</p>
            <MadeBy className="mt-4" />
          </div>
        </div>
      </div>
      <div className="hidden p-3 lg:col-span-7 lg:block">
        <div className="h-full overflow-hidden rounded-[28px] bg-secondary">
          {img && <img src={imgUrl(img, 1600)} alt="" className="h-full w-full object-cover" />}
        </div>
      </div>
    </div>
  );
}
